import { useTrack } from "@/lib/track";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutGroup, motion } from "framer-motion";
import { TopBar, Footer } from "@/components/z6/Chrome";
import { MenuCarousel } from "@/components/z6/MenuCarousel";
import { MenuPager } from "@/components/z6/MenuPager";
import { DishDetail } from "@/components/z6/DishDetail";
import { FilterPanel } from "@/components/z6/FilterPanel";
import { filterCourse, useFilters } from "@/context/FiltersContext";
import { FilteredResults } from "@/components/z6/FilteredResults";
import { getOnce } from "@/lib/api";
import { themeFor } from "@/lib/menuTheme";
import { useAmbient } from "@/lib/perf";

export default function Menus() {
  const [params, setParams] = useSearchParams();
  const [menus, setMenus] = useState([]);
  const [dishes, setDishes] = useState(null);
  const [drinks, setDrinks] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const { activeCount, clear, filters } = useFilters();
  const shown = useMemo(() => ["starter", "main", "dessert"].reduce((n, c) => n + filterCourse(dishes || [], c, filters).list.length, 0), [dishes, filters]);
  const setAccent = useAmbient((s) => s.setAccent);
  const active = params.get("menu");

  const load = useCallback(() => {
    setFailed(false);
    Promise.all([getOnce("/menus"), getOnce("/dishes"), getOnce("/dishes", { course: "beverage" })])
      .then(([m, d, b]) => { setMenus(m.menus); setDishes(d.dishes); setDrinks(b.dishes); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(load, [load]);
  const ready = !!(menus.length && dishes && drinks);
  useEffect(() => setAccent(themeFor(active).glow), [active, setAccent]);
  useTrack("menu_opened", { menu: active || undefined }, !!active);

  const menuNames = useMemo(() => Object.fromEntries(menus.map((m) => [m.id, m.short_name])), [menus]);
  const badgeFor = useCallback((d) => d.menus.map((m) => menuNames[m]).join(" · "), [menuNames]);
  const open = useCallback((d) => setSelected(d), []);

  return (
    <div data-testid="menu-page" className="min-h-screen">
      <TopBar onOpenFilters={() => setFilterOpen(true)} />
      <LayoutGroup>
        {/* Reserve a full viewport so the footer never jumps when the menu data arrives (category-page CLS). */}
        <main className="mx-auto min-h-[100svh] max-w-[1400px] px-5 pb-6 sm:px-8">
          {failed ? (
            <div data-testid="menu-error" role="alert" className="flex flex-wrap items-center gap-4 pt-10">
              <p className="font-ui text-[17px] text-[#0B2E44]">The menu didn't load. Check the connection and try again.</p>
              <button type="button" data-testid="menu-retry" onClick={load} className="z6-btn z6-btn--pink">Try <em>again</em></button>
            </div>
          ) : !ready && (activeCount > 0 || active) ? (
            <div data-testid="menu-skeleton" aria-busy="true" className="grid grid-cols-1 gap-6 pt-10 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((k) => <div key={k} className="aspect-[3/4] animate-pulse rounded-[22px] bg-white/60 motion-reduce:animate-none" />)}
            </div>
          ) : activeCount > 0 ? (
            <div className="space-y-10 pt-10">
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[15px] uppercase tracking-[0.35em] text-[#0B2E44]">Across all menus</p>
                  <h1 data-testid="filter-results-count" className="mt-2 font-display text-5xl text-[#0B2E44] sm:text-6xl">{shown} {shown === 1 ? "dish" : "dishes"} for you</h1>
                </div>
                <button data-testid="results-clear-all-btn" onClick={clear} className="font-ui min-h-[44px] rounded-full border border-[#0B2E44]/15 px-5 text-[15px] text-[#0B2E44]">Clear all</button>
              </motion.div>
              <FilteredResults dishes={dishes} filters={filters} onOpen={open} badgeFor={badgeFor} />
            </div>
          ) : active && menus.length ? (
            <MenuPager menus={menus} active={active} onChange={(id) => setParams({ menu: id })} dishes={dishes} drinks={drinks} onOpen={open} />
          ) : (
            <div className="pt-6 sm:pt-8">
              <motion.div data-testid="explore-heading" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 max-w-3xl">
                <p className="font-ui text-[15px] font-normal uppercase tracking-[0.32em] text-[#B8326C]">Seven menus, broths in every one</p>
                <h1 className="mt-3 font-display text-[clamp(34px,7vw,56px)] leading-[0.98] text-[#0B2E44] sm:mt-5">Choose the menu your body is <span className="italic">asking for</span></h1>
                <p className="font-ui mt-3 hidden text-[16px] text-[#33566B] sm:block">Tap a menu to enter. Or refine across every menu at once.</p>
                <Link to="/menu/all" data-testid="explore-full-menu-btn" className="z6-btn z6-btn--pink mt-4">Full <em>menu</em><span aria-hidden className="z6-arrow" /></Link>
              </motion.div>
              {menus.length > 0 && <div className="mt-4 sm:mt-8"><MenuCarousel menus={menus} onSelect={(k) => setParams({ menu: k })} /></div>}
            </div>
          )}
        </main>
        <DishDetail dish={selected} menuNames={menuNames} onOpenDish={setSelected} onClose={() => setSelected(null)} />
      </LayoutGroup>
      <Footer orderLinks />
      <FilterPanel open={filterOpen} onClose={() => setFilterOpen(false)} resultCount={shown} />
    </div>
  );
}
